/* eslint-disable max-lines */
import {
  Component, ChangeDetectionStrategy, OnDestroy, OnInit,
  ChangeDetectorRef, HostListener, Inject, ElementRef, ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { Meta, MetaDefinition, SafeResourceUrl, Title } from '@angular/platform-browser';

import { isNullOrUndefined } from '@app/util/util';
import { HomeItemDetailFacade } from '@home-item-detail/home-item-detail.facade';
import { HomeItemDetail, HomeItemDetailWidgetType } from '@home-item-detail/entities/home-item-detail';
import { HomeFlightOffers, HomeSection } from '@shared/home-sections/entities/home-data.response';
import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { AppFacade } from '@app/app.facade';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { DeeplinkPlacePipe } from '@app/pipes/deeplink-place.pipe';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { SALES_CHANNEL, SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { SearchType } from '@search-bar/entities/search-type';
import { mapSearchURLParams } from '@autos/auto-results/mappers/auto-results.mapper';
import { formatAutoDirections } from '@autos/auto-results/mappers/auto-search.mapper';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { EventDataLayer } from '@avo/interface/trigger-props';
import { IS_BROWSER, IS_SERVER } from '@app/modules/core/tokens/app.tokens';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { getURLWithoutAnchor } from '@ux/mappers/url-params.mapper';
import { AppWindow } from '@app/entities/app-window';
import { CLOSE_TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { mapWidgetsByDetail } from '@home-item-detail/mappers/home-item-widgets.mapper';
import { AvoService } from '@avo/services/avo.service';
import { FlightType } from '@search-bar/entities/flight-type';
import { StatsigExperimentsFacade } from '@statsig/statsig.facade';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { ModuleEntityGroup } from '@app/entities/modules-country';
import { countryModulesByWidgetsConfig } from '@home/mappers/home.mapper';
import { autoHomeRootRoute } from '@autos/auto-home/constants/routes';
import { productResultsRoute } from '@app/constants/app-routing.constants';

declare const window: AppWindow;
@Component({
  selector: 'app-home-item',
  templateUrl: './home-item.component.html',
  styleUrls: ['./home-item.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HomeItemComponent implements OnDestroy, OnInit {

  @ViewChild('footerHTML') footerHTML: ElementRef;
  @ViewChild('modalWebView') modalWebView: ElementRef;
  @ViewChild('iframeWebView') iframeWebView: ElementRef;

  public pageCode: string;
  public pageNameType: string;
  public typeImageFlight: string = IMAGE_TYPE.FLIGHT;
  public headerAnimation: boolean = false;
  public departurePlace: AutocompletePlace;
  public destinationCode: string;
  public arrivalPlace: SelectAutocompletePlace;
  public footerDynamic: string = '';
  public titleDocument: string;
  public urlWebView: string | SafeResourceUrl = null;
  public loadingIframe: boolean = true;
  public groupSearchTabs = false;

  protected AvoDispatch = new AvoDispatchOrchestrator();
  
  private scrollTopAnimation: number = 190;
  private subscriptions: Subscription[] = [];

  constructor(
    private appFacade: AppFacade,
    private route: ActivatedRoute,
    private router: Router,
    private readonly location: Location,
    private readonly facade: HomeItemDetailFacade,
    private readonly searchFacade: BaseSearchBarFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly placePipe: DeeplinkPlacePipe,
    private readonly triggerAmplitudeService: TriggerAmplitudeService,
    private readonly titleService: Title,
    private readonly metaService: Meta,
    private readonly avoService: AvoService,
    private readonly statsigExperimentsFacade: StatsigExperimentsFacade,
    @Inject(IS_BROWSER) public isBrowser: boolean,
    @Inject(IS_SERVER) public isServer: boolean
  ) {
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.route.queryParamMap.pipe(take(1)).subscribe(queries => {
        if (queries.get(SALES_CHANNEL)) {
          this.searchFacade.setSalesChannel(queries.get(SALES_CHANNEL));
        }
      }).unsubscribe();
      this.initialFetch();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.searchFacade.selectAirline(null);
    this.appFacade.resetCloseWebViewInDetail();
    this.facade.resetHomeItem();
    this.facade.resetFlightOffer();
    this.destinationCode = '';
    if (this.isBrowser) {
      this.titleService.setTitle(this.titleDocument);
    }
  }

  @HostListener('window:scroll', ['$event']) onWindowScroll(event: Event): void {
    this.isMobile$.subscribe(isMobile => {
      if (isMobile) {
        const offset = window['pageYOffset'] || document.documentElement.scrollTop || document.body.scrollTop || 0;
        this.headerAnimation = offset >= this.scrollTopAnimation;
        this.changeDetector.detectChanges();
      }
    }).unsubscribe();
  }

  get salesChannel$(): Observable<string> {
    return this.searchFacade.salesChannel$;
  }

  get source(): string {
    return this.route.snapshot.queryParamMap.get(SOURCE_PARAM);
  }

  get branchCode$(): Observable<string> {
    return this.searchFacade.searchData$.pipe(
      map(searchData => searchData.branchCode)
    );
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get homeItemDetail$(): Observable<HomeItemDetail> {
    return this.facade.homeItemDetail$;
  }

  get isLoadingHomeItemDetail$(): Observable<boolean> {
    return combineLatest([this.facade.isLoadingHomeItemDetail$, this.homeItemDetail$])
      .pipe(map(([isLoadingHomeItemDetail, homeItemDetail]) => isLoadingHomeItemDetail || isEmpty(homeItemDetail)));
  }

  get flightOffers$(): Observable<HomeFlightOffers[]> {
    return this.facade.flightOffers$;
  }

  get departurePlace$(): Observable<AutocompletePlace> {
    return this.searchFacade.departurePlace$;
  }

  get flightType$(): Observable<FlightType> {
    return this.searchFacade.flightType$;
  }

  get placeArrival$(): Observable<SelectAutocompletePlace> {
    return this.placePipe.transform(this.destinationCode).pipe(map(arrival => {
      const arrivalPlace = {
        code: arrival.cityCode,
        country: arrival.country,
        description: arrival.name,
        is_airport: arrival.is_airport,
        type: PlaceType.ARRIVAL
      } as SelectAutocompletePlace;
      return arrivalPlace;
    }));
  }

  get isPlace$(): Observable<boolean> {
    return this.placePipe.transform(this.destinationCode).pipe(map(arrival => arrival.cityCode ? true : false));
  }

  get widgets$(): Observable<HomeItemDetailWidgetType[]> {
    return this.homeItemDetail$.pipe(
      map(detail => mapWidgetsByDetail(detail))
    );
  }

  get hasWidgets$(): Observable<boolean> {
    return this.widgets$.pipe(map(widgets => !isEmpty(widgets)));
  }

  get countryModulesCollection$(): Observable<ModuleEntityGroup[]> {
    return this.widgets$.pipe(
      map(widgets => countryModulesByWidgetsConfig(widgets.join(), this.groupSearchTabs) as ModuleEntityGroup[])
    );
  }

  private initialFetch(): void {
    this.pageNameType = this.removeParamAnchor(this.route.snapshot.paramMap.get('name'));
    this.pageCode = this.removeParamAnchor(this.route.snapshot.paramMap.get('code'));
    if (!this.pageNameType || !this.pageCode) {
      this.appFacade.historyPop(); this.location.back();
    } else {
      this.subscriptions.push(
        this.homeItemDetail$.pipe(distinctUntilChanged()).subscribe((resp) => {
          this.destinationCode = resp?.destination_code;
          if (resp?.airline_code) {
            this.searchFacade.selectAirline(resp.airline_code);
          }
          this.destinationCode ? this.resetInputArrival() : this.destinationCodeFalse();
        }),
        combineLatest([
          this.departurePlace$.pipe(distinctUntilChanged()),
          this.flightType$.pipe(distinctUntilChanged()),
          this.isMobile$
        ]).pipe(
          filter(([departurePlace, flightType, isMobile]) => 
            !!departurePlace.code && !!this.destinationCode && !isMobile
          )
        ).subscribe(([departurePlace, flightType]) => {
          this.departurePlace = departurePlace;
          this.facade.fetchHomeFlightOffers(departurePlace.code, this.destinationCode, flightType);
        }),
        combineLatest(
          [this.route.params.pipe(distinctUntilChanged()),
            this.appFacade.user$,
            this.statsigExperimentsFacade.userExperiments$]
        ).pipe(
          tap(([params, user, experiments]) => {
            this.pageNameType = this.removeParamAnchor(params.name);
            this.pageCode = this.removeParamAnchor(params.code);
            const isPrime = user?.is_prime;
            const loyalty = user && user.loyalty ? user.loyalty.type : null;
            const levelPrime = user?.level_prime;
            const treatments = JSON.stringify(
              experiments.map(experiment => ({ split_name: experiment.name, treatment: experiment.treatment })));
            this.facade.fetchHomeItemDetail({
              pageCode: this.pageCode, 
              userId: user.id, 
              isPrime,
              loyalty,
              treatments,
              levelPrime
            });
          }),
          switchMap(() => this.avoService.getEventTriggerProps$(
            AVO_EVENTS.VIEW_TRAVEL_PAGE, { SOURCE: this.source, PAGE_CODE: this.pageCode }
          ).pipe(
            tap(trigger => {
              this.AvoDispatch.viewTravelPage({
                ...this.avoService.avoBaseProperties(trigger),
                source: this.avoService.getAvoSourceValueType(this.source),
                pageCode: this.pageCode,
              });
            })
          ))
        ).subscribe(),
        combineLatest([
          this.homeItemDetail$.pipe(distinctUntilChanged()),
          this.searchFacade.isSearchAirportForIp$.pipe(distinctUntilChanged())]
        ).pipe(
          filter(([detail, isSearchByIp]) => !isEmpty(detail) && !isSearchByIp),
          switchMap(() => this.widgets$.pipe(take(1), map(widgets => ({ widgets })))),
          filter(({ widgets }) => !!this.isFetchNear(widgets)),
          tap(() => this.searchFacade.fetchDefaultPlace(SearchType.HOME_FLIGHT))
        ).subscribe()
      );
      this.facade.clearFlightResults();
      this.facade.disabledFlightScroll();
      this.subscriptions.push(
        this.homeItemDetail$.subscribe(homeItemDetail => {
          this.writeDataSEO(homeItemDetail?.title, homeItemDetail?.metas, homeItemDetail?.footer_content);
        })
      );
    }
  }
  public resetInputArrival(): void {
    this.subscriptions.push(
      this.placeArrival$.subscribe(arrivalPlace => {
        if (arrivalPlace.code) {
          this.searchFacade.selectPlace(arrivalPlace);
          this.searchFacade.searchData$.subscribe(searchData => {
            const search = { ...searchData };
            if (!isNullOrUndefined(this.departurePlace)) {
              search.departurePlace = this.departurePlace;
            }
            search.arrivalPlace = arrivalPlace.code ? arrivalPlace : {} as SelectAutocompletePlace;
            this.searchFacade.saveSearchData(search, SearchBy.FLIGHTS);
          }).unsubscribe();
        } else this.clearSelectPlace();
      })
    );
  }
  public destinationCodeFalse(): void {
    this.clearSelectPlace();
    this.searchFacade.searchData$.subscribe(searchData => {
      const search = { ...searchData };
      if (this.departurePlace) {
        search.departurePlace = !isNullOrUndefined(search.departurePlace.code) ?
          search.departurePlace : {} as SelectAutocompletePlace;
        search.arrivalPlace = {} as SelectAutocompletePlace;
        this.searchFacade.saveSearchData(search, SearchBy.FLIGHTS);
      }
    }).unsubscribe();
  }
  public clearSelectPlace(): void {
    this.searchFacade.selectPlace({} as SelectAutocompletePlace);
  }

  public activeSlidePromos(homeSection: HomeSection): boolean {
    return homeSection.promos ? homeSection.promos.length > 1 : false;
  }

  public sectionsTypeText(homeSections: HomeSection[]): HomeSection[] {
    return homeSections && homeSections.length > 0 ? homeSections.filter(section => !isNullOrUndefined(section.text)) : [];
  }

  public sectionsExceptTypeText(homeSections: HomeSection[]): HomeSection[] {
    return homeSections && homeSections.length > 0 ? homeSections.filter(section => isNullOrUndefined(section.text)) : [];
  }

  public showLogo(logoUrl: string): boolean {
    return !isNullOrUndefined(logoUrl); 
  }

  public trackByType(index: number, type: HomeItemDetailWidgetType): HomeItemDetailWidgetType {
    return type;
  }

  public isFetchNear(widgets: HomeItemDetailWidgetType[]): boolean {
    return widgets.some(widget => widget === HomeItemDetailWidgetType.Air || HomeItemDetailWidgetType.Bus);
  }

  public autosSearch(): void {
    combineLatest([this.searchFacade.searchData$, this.searchFacade.autoDiffPlace$]).pipe(
      take(1),
      map(([data, autoDiffPlace]) => {
        const urlParams = mapSearchURLParams(data);
        return { urlParams, autoDiffPlace, branchCode: data.branchCode, ...formatAutoDirections(urlParams) };
      }),
      tap(({ urlParams, autoDiffPlace, branchCode, origin, destination }) => {
        const dest = (autoDiffPlace) ? destination : origin;
        this.router.navigate([
          `${autoHomeRootRoute}/${productResultsRoute}/` +
          `${origin}/${urlParams.departureDate}/` +
          `${urlParams.arrivalDate}/${dest}`],
        { queryParams: { source: this.pageCode || this.source, branch_code: branchCode } });
      })).subscribe().unsubscribe();
  }

  public trackEvent(event: EventDataLayer): void {
    this.triggerAmplitudeService.trackAmplitudeEventProps$(event.eventType, { ...event.eventProperties, SOURCE: this.source })
      .pipe(
        tap(trigger => triggerAmplitudeEventApp(trigger))
      ).subscribe().unsubscribe();
  }

  private writeDataSEO(title?: string, metas?: MetaDefinition[], footer_content?: string): void {
    if (metas) {
      this.metaService.addTags(metas);
    }
    if (footer_content) {
      this.footerDynamic = footer_content;
      setTimeout(() => {
        this.addEventHTML();
      }, CLOSE_TIME_TO_SLIDE);
    }
    if (title) {
      this.titleDocument = typeof window !== 'undefined' && document?.title !== title ? document?.title : this.titleDocument;
      this.titleService.setTitle(title);
    }

  }
  private removeParamAnchor(url: string): string {
    return getURLWithoutAnchor(url);
  }
  public addEventHTML(): void {
    const allLinks = this.footerHTML?.nativeElement?.querySelectorAll('a');
    if (allLinks) {
      allLinks?.forEach(link => {
        link.addEventListener('click', () => {
          this.isMobile$.subscribe(isMobile => {
            if (isMobile) {
              this.modalWebView.nativeElement.style.display = 'block';
              this.iframeWebView.nativeElement.src = link.href.replace('webview::', '');
            } else {
              window.open(link.href.replace('webview::', ''), '_blank');
            }
          }).unsubscribe();
        });
      });
    }
  }
  public toggleShowSwipeUp(): void {
    this.iframeWebView.nativeElement.src = '';
    this.modalWebView.nativeElement.style.display = 'none';
    this.loadingIframe = true;
  }
  public loadIframe(): void {
    this.loadingIframe = false;
  }
}
